import styled from 'styled-components';
import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button, CircularProgress } from '@material-ui/core';
import { InertiaLink } from '@inertiajs/inertia-react';
import { getCookieValue } from "../utils/getCookieValue";
import { Trans } from "../utils/i18n";

export default function Dropzone(props) {
  const [status, setStatus] = useState('initial'); // initial | validating | valid | uploading | uploaded | upload-failed
  const [responseMessage, setResponseMessage] = useState('');
const [errors, setErrors] = useState([]);
  const {acceptedFiles, getRootProps, getInputProps} = useDropzone({
      maxFiles: 1,
      disabled: !['initial'].includes(status),
  });

  useEffect(() => {
      const handleDrop = async () => {
          try {
              const formData = new FormData();
              formData.append('file', acceptedFiles[0]);

              setStatus('validating');
              const validationResponse = await fetch(route('queue.validate'), {
                  headers: {
                      'X-XSRF-TOKEN': getCookieValue('XSRF-TOKEN'),
                      'X-Requested-With': 'XMLHttpRequest',
                  },
                  method: 'POST',
                  body: formData,
              });

              const { message, errors } = await validationResponse.json();

              if (validationResponse.status === 200) {
                  setStatus('valid');
                  setResponseMessage(<Trans i18nKey="dropzone.validResponseMessage">The uploaded file is valid. You can import it by clicking "Import Queues"</Trans>);
                  setErrors([]);
              } else {
                  setStatus('initial');
                  setResponseMessage(message);
                  if(errors) {
                      setErrors(errors);
                  }
              }
          } catch (error) {
              setStatus('initial');
              setResponseMessage(<Trans i18nKey="dropzone.errorResponseMessage">An error occurred. Please try again!</Trans>);
              setErrors([]);
          }
      };

      if (acceptedFiles.length > 0) { // File selected
          handleDrop();
      }
  }, [acceptedFiles]);

  const handleClick = async() => {
    try {
        const formData = new FormData();
        formData.append('file', acceptedFiles[0]);

        setStatus('uploading');

        const uploadResponse = await fetch(route('queue.upload'), {
          headers: {
                  'X-XSRF-TOKEN': getCookieValue('XSRF-TOKEN'),
                },
              method: 'POST',
              body: formData,
          });

          if (uploadResponse.status === 200) {
              setStatus('uploaded');
          } else {
              setStatus('valid');
              setResponseMessage(<Trans i18nKey="dropzone.errorResponseMessage">An error occurred. Please try again!</Trans>);
          }
    } catch (error) {
        setStatus('valid');
        setResponseMessage(<Trans i18nKey="dropzone.errorResponseMessage">An error occurred. Please try again!</Trans>);
    }
  };

  return ['initial', 'validating', 'valid', 'uploading'].includes(status) ? (
    <StyledDropzone>
      <div {...getRootProps({ className: "dropzone" })}>
        <input {...getInputProps()} />
        <StyledDropField status={status}><Trans i18nKey="dropzone.dropFieldStatus">Drag 'n' drop one file here, or click to select a file</Trans></StyledDropField>
      </div>
      <StyledFilename><Trans i18nKey="dropzone.filename">Filename: </Trans>{acceptedFiles[0] ? acceptedFiles[0].path : <Trans i18nKey="dropzone.noFileSelected">No file selected</Trans>}</StyledFilename>
      {['validating', 'uploading'].includes(status) && <StyledCircularProgress size={25} />}
      {['initial', 'valid'].includes(status) && <h4>{responseMessage}</h4>}
        <ul>
            {
                errors.map && errors.map(item => <li>
                    <Trans i18nKey="dropzone.row">Row</Trans> {item.row}
                    <ul>
                        {
                            item.errors && item.errors.map(item => <li>{item}</li>)
                        }
                    </ul>
                </li>)
            }
            {
                !errors.map && Object.values(errors).map(item => <li>
                    {
                        item && item.join(', ')
                    }
                </li>)
            }
        </ul>
      <StyledButtons>
          <StyledLink href={route('queue.index')}>
              <Button variant="contained"><Trans i18nKey="dropzone.cancel">Cancel</Trans></Button>
          </StyledLink>
          <Button disabled={!['valid'].includes(status)} onClick={handleClick} variant="contained" color="secondary"><Trans i18nKey="dropzone.importQueues">Import queues</Trans></Button>
      </StyledButtons>
    </StyledDropzone>
  ) :  (
      <>
          <h4><Trans i18nKey="dropzone.successMessage">The file was successfully uploaded</Trans></h4>
          <StyledButtons>
              <StyledLink href={route('queue.index')}>
                  <Button variant="contained" color="secondary"><Trans i18nKey="dropzone.backToQueues">Back to Queues</Trans></Button>
              </StyledLink>
          </StyledButtons>
      </>
  );
};

const StyledDropzone = styled.div`
  margin: 70px 0;
`;

const StyledDropField = styled.p`
  padding: 100px 50px;
  border: 2px dashed currentColor;
  text-align: center;
  opacity: ${({ status }) => ['validating', 'valid', 'uploading'].includes(status) ? '50%' : '100%'};
  cursor: ${({ status }) => ['validating', 'valid', 'uploading'].includes(status) ? 'not-allowed' : 'pointer'};
`;

const StyledFilename = styled.h4`
  display: inline-block;
  margin-right: 30px;
  vertical-align: middle;
`;

const StyledCircularProgress = styled(CircularProgress)`
  vertical-align: middle;
`;

const StyledButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 30px;
  margin: 30px 0;
`;

const StyledLink = styled(InertiaLink)`
  text-decoration: none;
`;
